import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Slider } from '@/components/ui/slider';
import { getAIQuizV3 } from '@/services/generatedAPIs';
import { useSubjectList } from '@/hooks/use-subject-list';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AssignmentBasicInfo } from './AssignmentPage';
import { ROUTE } from '@/routes/constants';
import { Step, StepConnector } from '@/components/stepper';
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next';

import { useAsyncAction } from '@/hooks/use-async-action';

function AICompileAssignmentPageInner({ state }: { state: AssignmentBasicInfo }) {
  const { t, i18n } = useTranslation();
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const { subjectInfos } = useSubjectList();
  let [mcqCount, setMcqCount] = useState(4);
  let [frqCount, setFrqCount] = useState(4);
  let [bfqCount, setBfqCount] = useState(2);
  const [difficultyRange, setDifficultyRange] = useState([40, 60]);
  const navigate = useNavigate();

  return (
    <ContentLayout title={t("teacher.sysedit")}>
      <LoadingOverlay isLoading={isLoading} text={t('common.loading')} />
      <div className="flex items-center justify-center px-50 pt-5">
        <Step number={1} label={t("teacher.step1")} isActive={true} isCompleted={true} />
        <StepConnector isCompleted={true} />
        <Step number={2} label={[t("teacher.sysedit"), t("common.optional")].join('')} isActive={true} isCompleted={false} />
        <StepConnector isCompleted={false} />
        <Step number={3} label={[t("teacher.manualedit"), t("teacher.publish")].join(',')} isActive={false} isCompleted={false} />
      </div>
      <div className="flex flex-col pt-10 gap-6 items-center justify-center">
        {t("teacher.qtype")}
        <div className='flex flex-row gap-2 w-[50%]'>
          <div className='text-nowrap flex-1'>{t("common.mcq")}</div>
          <Slider
            defaultValue={[mcqCount]}
            max={10}
            step={1}
            className={"px-2 flex-1"}
            onValueChange={(value) => {
              setMcqCount(value[0]);
              mcqCount = value[0];
            }}
          />
          <span className='text-nowrap'>{mcqCount}{t("common.q")}</span>
        </div>

        <div className='flex flex-row gap-2 w-[50%]'>
          <span className='text-nowrap flex-1'>{t("common.bfq")}</span>
          <Slider
            defaultValue={[bfqCount]}
            max={10}
            step={1}
            className={"px-2 flex-1"}
            onValueChange={(value) => {
              setBfqCount(value[0]);
              bfqCount = value[0];
            }}
          />
          <span className='text-nowrap'>{bfqCount}{t("common.q")}</span>
        </div>

        <div className='flex flex-row gap-2 w-[50%]'>
          <span className='text-nowrap flex-1'>{t("common.frq")}</span>
          <Slider
            defaultValue={[frqCount]}
            max={10}
            step={1}
            className={"px-2 flex-1"}
            onValueChange={(value) => {
              setFrqCount(value[0]);
              frqCount = value[0];
            }}
          />
          <span className='text-nowrap'>{frqCount}{t("common.q")}</span>
        </div>

        <h2>{t("teacher.drange")}</h2>
        <div className='flex flex-col w-[40%] justify-center items-center'>
          {difficultyRange[0]} - {difficultyRange[1]}
          <Slider
            defaultValue={difficultyRange}
            max={100}
            step={1}
            className={"px-2"}
            onValueChange={(value) => {
              setDifficultyRange(value);
            }}
          />
          <div className='flex flex-row place-content-between w-full'>
            <span>{t("common.easy")}</span>
            <span>{t("common.median")}</span>
            <span>{t("common.hard")}</span>
          </div>
        </div>
        <span className='text-1xl'>{t('teacher.aiselectwarning')}</span>
        <div className="flex flex-row items-center justify-center gap-10">
          <Button variant="outline" onClick={() => {
            navigate(-1);
          }
          }>{t("teacher.prev")}</Button>
          <Button variant="default" className="bg-primary" loading={isLoading} onClick={() => {
            execute(async () => {
              const res = await getAIQuizV3({
                topicIds: [],
                knowledgePointIds: state.knowledgePointIds || [],
                libraryNames: state.libraryNames,
                language: state.language,
                mcqCount: mcqCount,
                frqCount: frqCount,
                bfqCount: bfqCount,
                minDifficulty: difficultyRange[0],
                maxDifficulty: difficultyRange[1]
              });
              if (res.code === 200 && res.data) {
                navigate(ROUTE.TEACHER.HOMEWORK_AI_EDIT_RESULT, {
                  state: {
                    ...state,
                    questionInfoList: res.data.questionInfoList,
                  }
                });
              } else {
                console.error('getAIQuizV3 error', res);
              }
            });
          }}>{t("teacher.sysgen")}</Button>
        </div>

      </div>
    </ContentLayout>
  );
};

const AICompileAssignmentPage = () => {
  const state = useLocation().state as AssignmentBasicInfo | null;
  const navigate = useNavigate();

  const isValidState = (s: any): s is AssignmentBasicInfo => {
    return !!s && Array.isArray(s.topicIds) && Array.isArray(s.classInfos) && !!s.dueDate && !!s.language;
  };

  useEffect(() => {
    if (!isValidState(state)) {
      navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
    }
  }, [state, navigate]);

  if (!isValidState(state)) {
    return <LoadingOverlay />;
  }

  return <AICompileAssignmentPageInner state={state} />;
};

export default AICompileAssignmentPage;
